import type { LocalizedText, MonsterRank } from "@/math";

export interface MonsterDef {
  id: string;
  name: LocalizedText;
  rank: MonsterRank;
  hp: number;
  atk: number;
  def: number;
  xp: number;
  gold: number;
  /** Sprite sheet key (16×16 frames, 4 per row); the idle animation is `${texture}-idle`. */
  texture: string;
  /** Portrait URL (38×38) for the battle panel. */
  faceset: string;
  lore: LocalizedText;
}

const def = (
  id: string,
  pl: string,
  en: string,
  rank: MonsterRank,
  stats: [hp: number, atk: number, def: number, xp: number, gold: number],
  sheet: string,
  lore: LocalizedText,
): MonsterDef => ({
  id,
  name: { pl, en },
  rank,
  hp: stats[0],
  atk: stats[1],
  def: stats[2],
  xp: stats[3],
  gold: stats[4],
  texture: `mon-${sheet.toLowerCase()}`,
  faceset: `assets/ninja/facesets/${sheet}.png`,
  lore,
});

export const MONSTERS: Record<string, MonsterDef> = {
  // ---- village & meadows ----
  blobble: def("blobble", "Blobble", "Blobble", "common", [18, 4, 0, 10, 5], "Slime2", {
    pl: "Galareta z zatroskaną miną. Powstaje z małych pomyłek w dodawaniu.",
    en: "A jelly with a worried face. Born from small slips in addition.",
  }),
  off_by_one_imp: def(
    "off_by_one_imp",
    "Chochlik O-Jeden",
    "Off-by-One Imp",
    "common",
    [24, 5, 1, 14, 7],
    "Cyclope",
    {
      pl: "Zawsze o jeden za dużo albo za mało. Kradnie monety, gdy się pomylisz.",
      en: "Always one too many or one too few. Steals coins when you slip.",
    },
  ),
  tin_knight: def("tin_knight", "Rycerz z Puszki", "Tin Knight", "elite", [40, 7, 2, 30, 15], "Fish", {
    pl: "Sardynka, która uciekła z przetwórni, założyła własną puszkę jako zbroję i przysięgła, że nigdy nie da się zapakować.",
    en: "A sardine that escaped the cannery, wears its own tin as armour and swore never to be packed again.",
  }),

  // ---- forest ----
  sporeling: def("sporeling", "Grzybek Zarodnik", "Sporeling", "elite", [32, 6, 1, 24, 12], "Mushroom", {
    pl: "Wyrósł z pomyłki schowanej pod dywan. Pilnuje skrzyń w lesie.",
    en: "Grew from a mistake swept under the rug. Guards chests in the forest.",
  }),
  owl: def("owl", "Sowa Sprawdzian", "Quiz Owl", "common", [26, 5, 1, 16, 8], "Owl", {
    pl: "Zadaje pytania w nocy i nie odlatuje bez odpowiedzi.",
    en: "Asks questions at night and will not fly away without an answer.",
  }),
  snake: def("snake", "Wąż Ułamek", "Fraction Snake", "common", [30, 6, 1, 18, 9], "Snake", {
    pl: "Długi jak licznik, kręty jak mianownik.",
    en: "Long as a numerator, twisty as a denominator.",
  }),
  bear: def("bear", "Niedźwiedź Bez Reszty", "Remainder Bear", "elite", [55, 8, 2, 50, 25], "Bear", {
    pl: "Dzieli wszystko na pół i nigdy nie zostawia reszty.",
    en: "Divides everything in half and never leaves a remainder.",
  }),

  // ---- ruins & mine ----
  skeleton_counter: def(
    "skeleton_counter",
    "Szkielet Rachmistrz",
    "Skeleton Counter",
    "elite",
    [48, 8, 2, 45, 22],
    "Skull",
    {
      pl: "Liczył tak długo, że została z niego sama czaszka. Nie oddaje skarbów bez walki.",
      en: "Counted for so long that only the skull is left. Does not give up treasure without a fight.",
    },
  ),
  mole: def("mole", "Kret Dzielnik", "Divisor Mole", "common", [32, 6, 2, 20, 10], "Mole", {
    pl: "Kopie tunele pod tabliczką mnożenia.",
    en: "Digs tunnels under the times table.",
  }),
  lizard: def("lizard", "Jaszczur Procent", "Percent Lizard", "common", [36, 7, 1, 22, 11], "Lizard", {
    pl: "Zmienia kolor o 25% przy każdym pytaniu.",
    en: "Changes colour by 25% with every question.",
  }),
  bat: def("bat", "Nietoperz Minus", "Minus Bat", "common", [28, 7, 0, 20, 10], "BlueBat", {
    pl: "Lata do góry nogami, więc wszystko, co ma, jest ujemne.",
    en: "Flies upside down, so everything it owns is negative.",
  }),

  // ---- coast ----
  octopus: def("octopus", "Ośmiornica Osiem", "Octo-Eight", "common", [40, 8, 2, 26, 13], "Octopus", {
    pl: "Osiem ramion, osiem pytań, osiem szans.",
    en: "Eight arms, eight questions, eight chances.",
  }),
  mollusc: def("mollusc", "Małż Nawias", "Bracket Clam", "elite", [60, 8, 4, 55, 28], "Mollusc", {
    pl: "Zamyka się jak nawias. Najpierw policz to, co w środku.",
    en: "Closes like a bracket. Work out what is inside first.",
  }),
  sardine_sergeant: def(
    "sardine_sergeant",
    "Sierżant Sardynka",
    "Sardine Sergeant",
    "boss",
    [110, 10, 3, 220, 90],
    "FishRed",
    {
      pl: "Dowódca Blaszanego Legionu. Nigdy się nie wycofuje i tobie też nie pozwoli.",
      en: "Commander of the Tin Legion. Never retreats and will not let you either.",
    },
  ),
};

/** Sprite sheet files (under assets/ninja/monsters) keyed by texture name. */
export const MONSTER_SHEETS: Record<string, string> = {
  "mon-slime": "Slime.png",
  "mon-slime2": "Slime2.png",
  "mon-slime3": "Slime3.png",
  "mon-slime4": "Slime4.png",
  "mon-fish": "Fish.png",
  "mon-fishred": "FishRed.png",
  "mon-cyclope": "Cyclope.png",
  "mon-mushroom": "Mushroom.png",
  "mon-skull": "Skull.png",
  "mon-owl": "Owl.png",
  "mon-snake": "Snake.png",
  "mon-bear": "Bear.png",
  "mon-mole": "Mole.png",
  "mon-lizard": "Lizard.png",
  "mon-bluebat": "BlueBat.png",
  "mon-octopus": "Octopus.png",
  "mon-mollusc": "Mollusc.png",
};
